import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import Footer from "./Footer";
import PSAAIAssistant from "../ai/PSAAIAssistant";
import useViewport from "../../hooks/useViewport";
import { color } from "../../styles/theme";

/**
 * App shell — was a fixed 232px sidebar + content flex row with zero
 * responsive behavior (see [[promoter-panel-chapter5-access-system]] et al.
 * for the rest of this pass); on a phone-width screen the sidebar alone ate
 * most of the viewport. Below the tablet breakpoint the sidebar becomes an
 * off-canvas drawer (Sidebar.jsx renders itself as fixed+translated) toggled
 * by a hamburger in Navbar, with a backdrop here to close on outside tap.
 */
const Layout = ({ children }) => {
  const { isCompact } = useViewport();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  // Close the drawer on every route change (link clicks inside Sidebar
  // navigate but don't themselves know to close it) and whenever the
  // viewport grows out of compact mode (so it doesn't stay "open" as
  // state while rendered statically at desktop width).
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!isCompact) setSidebarOpen(false);
    else setSidebarCollapsed(false);
  }, [isCompact]);

  return (
    <div style={styles.root}>
      <Navbar
        onToggleSidebar={isCompact ? () => setSidebarOpen((v) => !v) : undefined}
        onToggleSidebarCollapse={!isCompact ? () => setSidebarCollapsed((v) => !v) : undefined}
        sidebarCollapsed={sidebarCollapsed}
      />
      <div style={styles.body}>
        <Sidebar isCompact={isCompact} collapsed={sidebarCollapsed} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        {isCompact && sidebarOpen && (
          <div style={styles.backdrop} onClick={() => setSidebarOpen(false)} aria-hidden="true" />
        )}
        <div style={styles.contentArea}>
          {/* Extra bottom clearance beyond the normal padding — PSAAIAssistant
              below is a fixed bottom-right pill (right:24, bottom:24, ~50px
              tall) that sits on top of page content regardless of scroll
              position; without this, anything a page renders near its own
              bottom (pagination controls, action buttons) visually collides
              with it. */}
          <main style={{ ...styles.main, padding: isCompact ? "16px 14px 96px" : "28px 28px 104px" }}>{children}</main>
          <Footer />
        </div>
      </div>
      <PSAAIAssistant />
    </div>
  );
};

const styles = {
  root: { display: "flex", flexDirection: "column", minHeight: "100vh" },
  body: { display: "flex", flex: 1, minWidth: 0 },
  backdrop: {
    position: "fixed", top: 58, left: 0, right: 0, bottom: 0,
    background: "rgba(20,21,40,0.45)", zIndex: 149,
  },
  contentArea: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 58px)",
  },
  main: {
    flex: 1,
    background: color.bg,
    overflowY: "auto",
    minWidth: 0,
  },
};

export default Layout;
